/** The per-corner report card (#21): one lap's speeds, time-through,
 *  braking, balance and gear choice at each corner of the reference's set. */

import { column } from './columns';
import { brakeLength, brakePointDelta, gearHigh, LapTrace, measure, windows } from './cornerMetrics';
import { bisectLeft, interp, minOf } from './interp';
import { roundTo } from './round';
import type { Corner, CornerMeasure, CornerRow, Samples } from './types';

/** Slack allowed when judging whether a lap covers a corner window:
 *  reference corner distances land on the detection grid (2 m) and lap
 *  lengths differ by line taken, so an exit a metre past this lap's last
 *  sample is still "driven". */
export const GRID_TOLERANCE_M = 2;

function rounded(value: number | null | undefined, digits = 1): number | null {
  return value === null || value === undefined ? null : roundTo(value, digits) + 0;
}

export interface CornerReportOptions {
  /** This lap's own measurements, when the caller has already worked them
   *  out (the reference lap's, in a comparison). */
  measures?: ReadonlyMap<number, CornerMeasure>;
  /** The reference lap's measurements, which `brake_delta_m` is measured
   *  against. Left out, the report is the lap's own and has no delta. */
  reference?: ReadonlyMap<number, CornerMeasure>;
}

/** One lap's speeds, time-through, braking, balance and gear choice for each
 *  corner.
 *
 * `corners` is the REFERENCE lap's set (authored or detected), so every lap
 * in a comparison is measured through the same entry/exit windows — the same
 * distance-from-start approximation the delta chart already rests on. A
 * corner whose window runs past this lap's recording is omitted rather than
 * clamped: the time through part of a corner reported as the whole corner
 * would sort itself to the top of the report as a phantom gain.
 *
 * What the lap did at each corner is cornerMetrics' answer, the one the
 * race engineer speaks and the lap analysis document carries.
 */
export function cornerReport(
  corners: readonly Corner[],
  samples: Samples,
  options: CornerReportOptions = {},
): CornerRow[] {
  const dist = column(samples, 'dist') ?? [];
  const t = column(samples, 't') ?? [];
  const speed = column(samples, 'speed') ?? [];
  const n = Math.min(dist.length, t.length, speed.length);
  if (n < 2) return [];
  // Strictly increasing distance only — duplicates (pause edges, imports)
  // break interpolation, same as everywhere else distance is an axis.
  const keep = [0];
  for (let i = 1; i < n; i++) {
    if (dist[i]! > dist[keep[keep.length - 1]!]!) keep.push(i);
  }
  if (keep.length < 2) return [];
  const d = keep.map((i) => dist[i]!);
  const ts = keep.map((i) => t[i]!);
  const sp = keep.map((i) => speed[i]!);
  const first = d[0]!;
  const last = d[d.length - 1]!;

  const measures = options.measures ?? measure(new LapTrace(samples), windows(corners));
  const reference = options.reference;

  const out: CornerRow[] = [];
  for (const c of corners) {
    const entry = Number(c.entry_dist);
    const exit = Number(c.exit_dist);
    let timeS: number;
    let window: number[];
    if (entry <= exit) {
      if (entry < first - GRID_TOLERANCE_M || exit > last + GRID_TOLERANCE_M) {
        continue; // this lap never drove the full window
      }
      timeS = interp(d, ts, exit) - interp(d, ts, entry);
      const lo = bisectLeft(d, entry);
      const hi = bisectLeft(d, exit);
      window = sp.slice(lo, hi);
      if (window.length === 0) window = [interp(d, sp, (entry + exit) / 2)];
    } else {
      // A start/finish-stitched corner wraps the lap boundary: its time
      // is the tail of this lap plus the head of it — so the lap must
      // cover BOTH: a short lap that never reached the entry would
      // otherwise clamp to its last sample and report the head alone,
      // a phantom gain sorted straight to the top of the card.
      if (exit > last + GRID_TOLERANCE_M || entry > last + GRID_TOLERANCE_M) continue;
      timeS = ts[ts.length - 1]! - interp(d, ts, entry) + (interp(d, ts, exit) - ts[0]!);
      const lo = bisectLeft(d, entry);
      const hi = bisectLeft(d, exit);
      window = sp.slice(lo).concat(sp.slice(0, hi));
      if (window.length === 0) window = [sp[0]!];
    }
    const number = Math.trunc(Number(c.n));
    const mine = measures.get(number);
    const theirs = reference !== undefined ? reference.get(number) : undefined;
    out.push({
      n: number,
      entry_speed: roundTo(interp(d, sp, entry), 1),
      min_speed: roundTo(minOf(window), 1),
      exit_speed: roundTo(interp(d, sp, exit), 1),
      time_ms: roundTo(timeS * 1000, 1),
      brake_on: rounded(mine ? mine.brake_on : null),
      brake_off: rounded(mine ? mine.brake_off : null),
      brake_peak: rounded(mine ? mine.brake_peak : null),
      brake_dist: rounded(brakeLength(mine)),
      brake_delta_m: rounded(brakePointDelta(mine, theirs)),
      slip_peak: rounded(mine ? mine.slip_peak : null, 2),
      slip_mean: rounded(mine ? mine.slip_mean : null, 2),
      suggested_gear: mine ? mine.suggested_gear : null,
      gear_high: gearHigh(mine),
    });
  }
  return out;
}
